import { DropdownMenu } from '@design/components/dropdown-menu/DropdownMenu';
import {
  DropdownEntry,
  DropdownGroup,
} from '@design/components/dropdown-menu/dropdown-group/DropdownGroup';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import {
  TableCell,
  TableHeader,
  TableHeaderIcons,
} from '@design/components/table';
import { Tooltip } from '@design/components/tooltip/Tooltip';
import { isElementScrollbarVisible, useScrollListener } from '@helpers';
import {
  Column,
  flexRender,
  functionalUpdate,
  getCoreRowModel,
  SortDirection,
  useReactTable,
} from '@tanstack/react-table';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { queryKeys } from '@terminal/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { ResetAllButton } from '@terminal/views/screener/reset-all-button/ResetAllButton';
import { ResultsEmptyState } from '@terminal/views/screener/results-empty-state/ResultsEmptyState';
import { AssetOverviewDrawer } from '@terminal/widgets/asset-overview-widget/AssetOverviewDrawer';
import { RankingMethodologySection } from '@terminal/widgets/knowledge-graph-widget/components/theme-exposure-tab/theme-exposure-table/ranking-methodology-section/RankingMethodologySection';
import { useLayoutEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { isCellNumber } from '../../earnings/components/earnings-table/utils/utils';
import { usePerformanceFiltersData } from '../hooks/usePerformanceFiltersData';
import { usePerformanceStore } from '../hooks/usePerformanceStore';
import {
  PerformanceTableData,
  usePerformanceTable,
} from '../hooks/usePerformanceTable';
import { getAlignment, getColumns } from '../table-utils';
import styles from './PerformanceTable.module.scss';

export const PerformanceTable = () => {
  const performanceTableRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation([
    'widget',
    'screener',
    'chat',
    'common',
    'watchlistManager',
  ]);
  const {
    tableData,
    columnsMeta,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetching: isTableFetching,
  } = usePerformanceTable();
  const {
    tableSorting,
    companiesTheme,
    hash32ThemeId,
    clearAllActiveFilterOptions,
    setTableSorting,
  } = usePerformanceStore(state => ({
    tableSorting: state.tableSorting,
    companiesTheme: state.companiesTheme,
    hash32ThemeId: state.hash32ThemeId,
    setTableSorting: state.setTableSorting,
    clearAllActiveFilterOptions: state.clearAllActiveFilterOptions,
  }));
  const { missingEntities, isFetching: isFiltersFetching } =
    usePerformanceFiltersData();
  const { trackAoDrawerOpen } = usePosthogEvents();
  const { setQueryParams, deleteQueryParam, params } = useQueryParams();

  const columns = useMemo(
    () =>
      columnsMeta.length && !isFiltersFetching
        ? getColumns({
            hash32ThemeId,
            columnsMeta,
            t,
            missingEntityTags: missingEntities.map(e => e.tag),
          })
        : [],
    [
      columnsMeta.map(c => c.field).toString(),
      isFiltersFetching,
      missingEntities,
    ]
  );

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting: tableSorting,
    },
    enableMultiSort: false,
    manualSorting: true,
    onSortingChange: updaterFunction => {
      const newValue = functionalUpdate(updaterFunction, tableSorting);
      setTableSorting(newValue);
    },
  });

  useScrollListener(
    performanceTableRef,
    () => {
      if (hasNextPage) {
        fetchNextPage();
      }

      return hasNextPage;
    },
    [hasNextPage],
    { isGlobalScrollListened: false, offset: 20 }
  );

  useLayoutEffect(() => {
    const hasScroll = isElementScrollbarVisible(
      performanceTableRef,
      'vertical'
    );

    if (hasScroll || !performanceTableRef.current || !companiesTheme.length) {
      return;
    }

    fetchNextPage();
  }, [performanceTableRef, fetchNextPage]);

  if (isError) {
    throw new Error('Screener data fetching error');
  }

  const tableRows = table.getRowModel().rows;

  if (!tableRows.length && isTableFetching) {
    return (
      <SkeletonLoader
        data-testid="performance-table-loading"
        areas={[
          {
            styles: {
              width: '100%',
              height: '200px',
              borderRadius: '8px',
            },
          },
        ]}
      />
    );
  }

  const getDropdownItems = (
    column: Column<PerformanceTableData, unknown>,
    sortOrder: false | SortDirection
  ) => {
    const enabledKeys = sortOrder
      ? ['sortUp', 'sortDown', 'clearSort']
      : ['sortUp', 'sortDown'];

    const items = [
      {
        label: t('common:table.dropdown_ascending'),
        iconName: 'SortUp',
        key: 'sortUp',
        onClick: () => column.toggleSorting(false),
      },
      {
        label: t('common:table.dropdown_descending'),
        iconName: 'SortDown',
        key: 'sortDown',
        onClick: () => column.toggleSorting(true),
      },
      {
        label: t('common:table.dropdown_clearSort'),
        iconName: 'ClearSort',
        key: 'clearSort',
        onClick: column.clearSorting,
      },
    ];

    return items.filter(i => enabledKeys.includes(i.key)) as DropdownEntry[];
  };

  const onRowClick = (entityTag?: string) => {
    if (entityTag) {
      trackAoDrawerOpen(POSTHOG_TRACKING_SOURCES.THEME_OVERVIEW, [entityTag]);
      setQueryParams({
        [queryKeys.activeDrawer]: queryKeys.entityTag,
        [queryKeys.entityTag]: entityTag,
      });
    }
  };

  return (
    <>
      <div className={styles.tableScrollContainer} ref={performanceTableRef}>
        {tableRows.length ? (
          <table className={styles.tableRoot}>
            <thead>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header, index) => {
                    const sortOrder = header.column.getIsSorted();
                    const alignment = getAlignment(index);
                    const dropdownItems = getDropdownItems(
                      header.column,
                      sortOrder
                    );
                    return (
                      <DropdownMenu
                        key={header.id}
                        triggerNode={
                          <TableHeader
                            className={styles.tableHeader}
                            key={header.column.id}
                            alignment={alignment}
                          >
                            <TableHeaderIcons
                              isFiltered={header.column.getIsFiltered()}
                              sortDirection={sortOrder}
                              alignment={alignment}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                            </TableHeaderIcons>
                          </TableHeader>
                        }
                      >
                        <DropdownGroup items={dropdownItems} />
                      </DropdownMenu>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {tableRows.map(row => {
                const tag = row.original.entity?.tag;
                const isExcludedEntity =
                  !tag || missingEntities.map(e => e.tag).includes(tag);
                return (
                  <Tooltip
                    disabled={!isExcludedEntity}
                    label={t(
                      'widget:themeOverview.tabs.performance.assetReturnNotIncluded'
                    )}
                    key={row.id}
                  >
                    <tr className={styles.tableRow}>
                      {row.getVisibleCells().map((cell, index) => {
                        const alignment = getAlignment(index);

                        return (
                          <TableCell
                            key={cell.id}
                            isNumber={isCellNumber(cell.column.id)}
                            alignment={alignment}
                            onClick={() =>
                              onRowClick(cell.row.original.entity?.tag)
                            }
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        );
                      })}
                    </tr>
                  </Tooltip>
                );
              })}
              {hasNextPage ? (
                <tr key="loading-row">
                  {tableRows[0].getVisibleCells().map(cell => (
                    <TableCell key={cell.id} isNumber={false}>
                      <SkeletonLoader
                        areas={[
                          {
                            styles: {
                              width: '100%',
                              height: '20px',
                              borderRadius: '8px',
                            },
                          },
                        ]}
                      />
                    </TableCell>
                  ))}
                </tr>
              ) : (
                <tr data-testid="footer-row">
                  <TableCell
                    className={styles.footerRow}
                    colspan={table.getHeaderGroups()[0].headers.length}
                    isNumber={false}
                  >
                    <RankingMethodologySection rankingType="themeToCompany" />
                  </TableCell>
                </tr>
              )}
            </tbody>
          </table>
        ) : (
          <ResultsEmptyState
            resetButton={
              <ResetAllButton
                resetActiveFilterOptions={() => clearAllActiveFilterOptions(t)}
              />
            }
          />
        )}
      </div>
      <AssetOverviewDrawer
        entityTag={params.entityTag ?? ''}
        closeDrawer={() =>
          deleteQueryParam([queryKeys.activeDrawer, queryKeys.entityTag])
        }
        isOpen={
          params.activeDrawer === queryKeys.entityTag && !!params.entityTag
        }
      />
    </>
  );
};
